/*
  The ninja drawing system, ported verbatim from the prototype. One base ninja
  (body, belt, head, mask, eyes) plus per-rank layers drawn behind the body
  (`back`), after the body (`afterBody`), before and after the head, and in
  front of everything (`front`). Output is checked byte-for-byte against
  goldens generated from the prototype (see scripts/golden.mjs), so keep the
  template strings exactly as they are.

  v1.1 adds optional card variants (content/variants.ts) and belt stripes.
  They only insert extra strings next to the existing layers, so a plain
  ninja is still byte-for-byte the prototype's.
*/
import { MODE_INFO } from "../content/modes";
import type { Rank } from "../content/ranks";
import type { Variant, VariantLayer } from "../content/variants";

export function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const t = amt < 0 ? 0 : 255,
    p = Math.abs(amt);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round((t - v) * p + v));
  return "#" + c.map((v) => v.toString(16).padStart(2, "0")).join("");
}

function shuriken(cx: number, cy: number, s: number, rot: number): string {
  const k = s * 0.28;
  const d = `M0 ${-s} L${k} ${-k} L${s} 0 L${k} ${k} L0 ${s} L${-k} ${k} L${-s} 0 L${-k} ${-k}Z`;
  return `<g transform="translate(${cx} ${cy}) rotate(${rot})"><path d="${d}" fill="#D3DAE5" stroke="#5F6B80" stroke-width="2.5" stroke-linejoin="round"/><circle r="${s * 0.18}" fill="#5F6B80"/></g>`;
}

function bolt(flip: boolean): string {
  const pts: [number, number][] = [
    [30, 36],
    [56, 36],
    [42, 68],
    [62, 68],
    [26, 122],
    [38, 82],
    [20, 82],
  ];
  const pt = ([x, y]: [number, number]) => {
    const nx = 14 + (x - 20) * 0.85;
    return `${flip ? 200 - nx : nx} ${y + 44}`;
  };
  return `<path d="M${pts.map(pt).join(" L")}Z" fill="#FFD23F" stroke="#E0A10B" stroke-width="3" stroke-linejoin="round"/>`;
}

export type NinjaArtInput = Pick<Rank, "name" | "art" | "suit" | "band" | "eyes" | "colour">;

export interface NinjaSvgOptions {
  locked?: boolean;
  /** Unique id prefix for the clipPath and filter, so several ninjas can share a page. */
  id: string;
  /** A strand's look of the card. Classic (no layers, no colour change) draws the plain ninja. */
  variant?: Variant | undefined;
  /** Stripes (0–3) on the belt, earned on the way to the next rank. */
  stripes?: number | undefined;
}

interface Palette {
  suit: string;
  dark: string;
  band: string;
  bg: string;
  skin: string;
  ink: string;
}

/** Four-point sparkle. */
function sparkle(cx: number, cy: number, s: number, fill: string): string {
  const k = s * 0.3;
  return `<path d="M${cx} ${cy - s} L${cx + k} ${cy - k} L${cx + s} ${cy} L${cx + k} ${cy + k} L${cx} ${cy + s} L${cx - k} ${cy + k} L${cx - s} ${cy} L${cx - k} ${cy - k}Z" fill="${fill}"/>`;
}

/** A faint copy of the ninja, shifted sideways (the Clone look). */
function ghost(dx: number, p: Palette): string {
  return `<g transform="translate(${100 + dx} 112) scale(.7) translate(-100 -116)" opacity=".6"><path d="M66 164 Q62 118 100 116 Q138 118 134 164Z" fill="${p.suit}"/><rect x="78" y="148" width="18" height="36" rx="8" fill="${p.dark}"/><rect x="104" y="148" width="18" height="36" rx="8" fill="${p.dark}"/><circle cx="100" cy="78" r="50" fill="${p.suit}"/><rect x="54" y="44" width="92" height="12" rx="6" fill="${p.band}"/><rect x="58" y="66" width="84" height="29" rx="14.5" fill="${p.skin}"/></g>`;
}

function dagger(flip: boolean, p: Palette): string {
  const f = (x: number) => (flip ? 200 - x : x);
  return `<path d="M${f(60)} 140 L${f(22)} 90 L${f(29)} 86 L${f(66)} 136Z" fill="#EEF2F7" stroke="#7D8AA0" stroke-width="2.5" stroke-linejoin="round"/><line x1="${f(54)}" y1="134" x2="${f(70)}" y2="144" stroke="#F5B82E" stroke-width="5" stroke-linecap="round"/><circle cx="${f(64)}" cy="146" r="11" fill="${p.dark}"/>`;
}

/** Extra drawings for card variants, by where they go in the stack. */
function variantLayer(
  layer: VariantLayer,
  p: Palette,
): { back?: string; body?: string; front?: string } {
  switch (layer) {
    case "moon":
      return {
        back:
          `<circle cx="158" cy="40" r="17" fill="#F4F1D0"/><circle cx="166" cy="34" r="15" fill="${p.bg}"/>` +
          sparkle(38, 38, 6, "#F4F1D0") +
          sparkle(58, 18, 4, "#F4F1D0") +
          sparkle(176, 96, 4, "#F4F1D0"),
      };
    case "smoke":
      return {
        front: [
          [58, 186, 24],
          [100, 190, 28],
          [142, 186, 24],
        ]
          .map(
            ([x, y, rx]) =>
              `<ellipse cx="${x}" cy="${y}" rx="${rx}" ry="8" fill="#9C8BD9" opacity=".6"/>`,
          )
          .join(""),
      };
    case "clones":
      return { back: ghost(-54, p) + ghost(54, p) };
    case "aura": {
      const arc = 2 * Math.PI * 88;
      const q = arc / 4;
      const ring = (["add", "sub", "mul", "div"] as const)
        .map(
          (m, i) =>
            `<circle cx="100" cy="100" r="88" fill="none" stroke="${MODE_INFO[m].colour}" stroke-width="9" stroke-dasharray="${q.toFixed(1)} ${(arc - q).toFixed(1)}" stroke-dashoffset="${(-q * i).toFixed(1)}" opacity=".85"/>`,
        )
        .join("");
      return {
        back:
          `<circle cx="100" cy="100" r="78" fill="#FFFFFF" opacity=".35"/>` +
          ring +
          sparkle(30, 44, 7, MODE_INFO.add.colour) +
          sparkle(172, 52, 6, MODE_INFO.sub.colour) +
          sparkle(26, 150, 6, MODE_INFO.mul.colour) +
          sparkle(176, 150, 7, MODE_INFO.div.colour),
      };
    }
    case "split":
      return {
        body: `<path d="M100 116 Q138 118 135 146 L100 146Z" fill="${p.band}"/><path d="M100 140 L114 118" stroke="${p.dark}" stroke-width="4" fill="none"/><circle cx="136" cy="146" r="11" fill="${p.dark}"/>`,
      };
    case "blades":
      return { front: dagger(false, p) + dagger(true, p) };
  }
}

/** White bars on the belt, one per stripe. */
function beltStripes(n: number, band: string): string {
  return Array.from(
    { length: Math.max(0, Math.min(3, Math.floor(n))) },
    (_, i) =>
      `<rect x="${110 + i * 7}" y="145" width="4.5" height="12" rx="1" fill="#FFFFFF" stroke="${shade(band, -0.45)}" stroke-width="1"/>`,
  ).join("");
}

export function ninjaSvg(
  input: NinjaArtInput,
  { locked = false, id, variant, stripes = 0 }: NinjaSvgOptions,
): string {
  const r: NinjaArtInput = variant?.eyes ? { ...input, eyes: variant.eyes } : input;
  const suit = variant?.suitShade ? shade(r.suit, variant.suitShade) : r.suit,
    dark = shade(suit, -0.35),
    band = r.band;
  const bg = shade(r.colour, variant?.bgShade ?? 0.72),
    skin = "#F7CBA3",
    ink = "#1D2340";
  const glowCol = variant?.glow ?? "#FFD23F";
  const palette: Palette = { suit, dark, band, bg, skin, ink };
  let vBack = "",
    vBody = "",
    vFront = "";
  for (const layer of variant?.layers ?? []) {
    const l = variantLayer(layer, palette);
    vBack += l.back ?? "";
    vBody += l.body ?? "";
    vFront += l.front ?? "";
  }
  vBody += beltStripes(stripes, band);
  let back = "",
    afterBody = "",
    beforeHead = "",
    afterHead = "",
    front = "";

  switch (r.art) {
    case "seedling":
      afterHead = `<path d="M100 32 Q97 20 101 8" stroke="#3F8F2F" stroke-width="5" fill="none" stroke-linecap="round"/>
        <ellipse cx="89" cy="12" rx="12" ry="6.5" fill="#7DC247" stroke="#3F8F2F" stroke-width="2" transform="rotate(-25 89 12)"/>
        <ellipse cx="113" cy="14" rx="12" ry="6.5" fill="#7DC247" stroke="#3F8F2F" stroke-width="2" transform="rotate(25 113 14)"/>`;
      break;
    case "bamboo":
      back = `<g transform="rotate(-35 100 110)"><rect x="93" y="-6" width="14" height="220" rx="7" fill="#8CC152" stroke="#4E7F2A" stroke-width="3"/>
        ${[35, 80, 125, 170].map((y) => `<line x1="93" x2="107" y1="${y}" y2="${y}" stroke="#4E7F2A" stroke-width="4"/>`).join("")}
        <ellipse cx="116" cy="30" rx="14" ry="5" fill="#8CC152" stroke="#4E7F2A" stroke-width="2" transform="rotate(30 116 30)"/></g>`;
      break;
    case "cub":
      beforeHead = `<path d="M56 62 L52 16 L90 36Z" fill="${suit}"/><path d="M62 52 L60 28 L80 40Z" fill="#F48FB8"/>
        <path d="M144 62 L148 16 L110 36Z" fill="${suit}"/><path d="M138 52 L140 28 L120 40Z" fill="#F48FB8"/>`;
      back = `<path d="M126 168 Q178 176 168 128 Q162 108 146 118" stroke="${suit}" stroke-width="13" fill="none" stroke-linecap="round"/>`;
      afterHead = `<path d="M60 86 L46 83 M60 91 L47 94 M140 86 L154 83 M140 91 L153 94" stroke="${ink}" stroke-width="2" stroke-linecap="round"/>`;
      break;
    case "silent":
      afterBody = `<path d="M74 124 Q42 108 14 126 Q38 126 26 146 Q54 134 78 134Z" fill="${band}" stroke="${shade(band, -0.3)}" stroke-width="2"/>
        <path d="M68 118 Q100 132 132 118 L132 130 Q100 144 68 130Z" fill="${band}" stroke="${shade(band, -0.3)}" stroke-width="2"/>`;
      front = (
        [
          [66, 180, 13],
          [88, 188, 15],
          [114, 188, 14],
          [136, 180, 12],
          [50, 172, 8],
          [152, 170, 8],
        ] as const
      )
        .map(([x, y, rr]) => `<circle cx="${x}" cy="${y}" r="${rr}" fill="#DDE3EF" opacity=".95"/>`)
        .join("");
      break;
    case "star":
      front =
        shuriken(152, 138, 24, 20) +
        shuriken(40, 48, 14, -15) +
        `<path d="M56 40 L78 30 M58 52 L82 48" stroke="${ink}" stroke-width="3" stroke-linecap="round" opacity=".5"/>
         <path d="M160 40 l4 10 10 4 -10 4 -4 10 -4 -10 -10 -4 10 -4Z" fill="#F5B82E"/>`;
      break;
    case "storm":
      back =
        bolt(false) +
        bolt(true) +
        `<path d="M30 60 Q48 50 62 58 M140 30 Q156 20 170 28" stroke="#8FD0E8" stroke-width="4" fill="none" stroke-linecap="round"/>`;
      break;
    case "fire":
      back = `<path d="M36 184 Q16 124 48 92 Q48 124 68 112 Q58 70 100 36 Q98 78 128 90 Q138 62 152 70 Q186 112 164 184Z" fill="#FF8A2B"/>
        <path d="M60 184 Q48 140 70 120 Q74 140 86 132 Q84 100 104 80 Q108 110 126 118 Q132 104 140 110 Q156 140 142 184Z" fill="#FFD23F"/>`;
      front = `<path d="M142 136 L184 28 L192 32 L150 140Z" fill="#EEF2F7" stroke="#7D8AA0" stroke-width="2.5" stroke-linejoin="round"/>
        <ellipse cx="146" cy="138" rx="12" ry="4.5" fill="#F5B82E" stroke="#9A6E0A" stroke-width="2" transform="rotate(-22 146 138)"/>
        <line x1="143" y1="143" x2="136" y2="160" stroke="${ink}" stroke-width="8" stroke-linecap="round"/>
        <circle cx="136" cy="148" r="11" fill="${dark}"/>`;
      break;
    case "dragon":
      back = `<path d="M186 178 Q148 194 140 160 Q192 142 176 96 Q166 62 190 44" stroke="#3DBE8B" stroke-width="20" fill="none" stroke-linecap="round"/>
        <path d="M186 178 Q148 194 140 160 Q192 142 176 96 Q166 62 190 44" stroke="#136B4C" stroke-width="7" fill="none" stroke-dasharray="2 13" stroke-linecap="round"/>
        <path d="M24 120 Q10 90 30 70" stroke="#3DBE8B" stroke-width="12" fill="none" stroke-linecap="round"/>`;
      afterHead = `<path d="M70 42 Q58 16 36 10 Q54 28 60 50Z" fill="#F7E7B6" stroke="#C9A961" stroke-width="2"/>
        <path d="M130 42 Q142 16 164 10 Q146 28 140 50Z" fill="#F7E7B6" stroke="#C9A961" stroke-width="2"/>`;
      front = `<circle cx="64" cy="138" r="14" fill="#A8F5E3" opacity=".5"/><circle cx="64" cy="138" r="10" fill="#9FF3E0" stroke="#fff" stroke-width="2"/>
        <circle cx="64" cy="150" r="10" fill="${dark}"/>`;
      break;
    case "legend":
      back =
        Array.from(
          { length: 12 },
          (_, i) =>
            `<path d="M100 100 L${100 + 110 * Math.cos(((i * 30 - 4) * Math.PI) / 180)} ${100 + 110 * Math.sin(((i * 30 - 4) * Math.PI) / 180)} L${100 + 110 * Math.cos(((i * 30 + 4) * Math.PI) / 180)} ${100 + 110 * Math.sin(((i * 30 + 4) * Math.PI) / 180)}Z" fill="#FFD23F" opacity=".45"/>`,
        ).join("") +
        `<line x1="50" y1="16" x2="98" y2="112" stroke="${ink}" stroke-width="9" stroke-linecap="round"/>
         <line x1="150" y1="16" x2="102" y2="112" stroke="${ink}" stroke-width="9" stroke-linecap="round"/>
         <line x1="50" y1="16" x2="62" y2="40" stroke="#F5B82E" stroke-width="9" stroke-dasharray="3 4"/>
         <line x1="150" y1="16" x2="138" y2="40" stroke="#F5B82E" stroke-width="9" stroke-dasharray="3 4"/>`;
      afterBody = `<path d="M100 118 L100 146" stroke="#F5B82E" stroke-width="4"/>`;
      break;
  }

  let eyes: string;
  const eyeCol = r.eyes === "glow" ? glowCol : ink;
  if (r.eyes === "happy") {
    eyes = `<ellipse cx="84" cy="80" rx="7.5" ry="10" fill="${ink}"/><ellipse cx="116" cy="80" rx="7.5" ry="10" fill="${ink}"/>
      <circle cx="87" cy="76" r="3.2" fill="#fff"/><circle cx="119" cy="76" r="3.2" fill="#fff"/>
      <circle cx="68" cy="89" r="5" fill="#F48FB8" opacity=".6"/><circle cx="132" cy="89" r="5" fill="#F48FB8" opacity=".6"/>`;
  } else {
    const brows =
      r.eyes === "normal" ? "M75 70 L92 73 M108 73 L125 70" : "M73 66 L93 75 M107 75 L127 66";
    const glow =
      r.eyes === "glow"
        ? `<ellipse cx="84" cy="81" rx="12" ry="9" fill="${glowCol}" opacity=".35"/><ellipse cx="116" cy="81" rx="12" ry="9" fill="${glowCol}" opacity=".35"/>`
        : "";
    eyes = `${glow}<ellipse cx="84" cy="81" rx="6.5" ry="${r.eyes === "normal" ? 8 : 7}" fill="${eyeCol}"/><ellipse cx="116" cy="81" rx="6.5" ry="${r.eyes === "normal" ? 8 : 7}" fill="${eyeCol}"/>
      <circle cx="86" cy="78" r="2.4" fill="#fff"/><circle cx="118" cy="78" r="2.4" fill="#fff"/>
      <path d="${brows}" stroke="${ink}" stroke-width="${r.eyes === "normal" ? 4 : 5}" stroke-linecap="round"/>`;
  }

  return `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${r.name} ninja">
    <defs><clipPath id="${id}"><circle cx="100" cy="78" r="50"/></clipPath>
      <filter id="${id}s"><feColorMatrix type="matrix" values="0 0 0 0 .11  0 0 0 0 .13  0 0 0 0 .25  0 0 0 1 0"/></filter></defs>
    <circle cx="100" cy="100" r="96" fill="${locked ? "#C9D0E0" : bg}"/>
    <g ${locked ? `filter="url(#${id}s)"` : ""}>
    ${vBack}${back}
    <ellipse cx="100" cy="186" rx="42" ry="7" fill="#000" opacity=".12"/>
    <rect x="78" y="148" width="18" height="36" rx="8" fill="${dark}"/>
    <rect x="104" y="148" width="18" height="36" rx="8" fill="${dark}"/>
    <path d="M66 164 Q62 118 100 116 Q138 118 134 164Z" fill="${suit}"/>
    <path d="M86 118 L100 140 L114 118" stroke="${dark}" stroke-width="4" fill="none"/>
    <rect x="66" y="146" width="68" height="10" fill="${band}"/>
    <path d="M100 156 L92 170 M100 156 L108 170" stroke="${band}" stroke-width="5" stroke-linecap="round"/>
    <circle cx="64" cy="146" r="11" fill="${dark}"/>
    <circle cx="136" cy="146" r="11" fill="${dark}"/>
    ${afterBody}${vBody}
    ${beforeHead}
    <path d="M146 50 Q166 38 186 44 Q168 50 176 58 Q160 54 146 60Z" fill="${band}"/>
    <path d="M146 54 Q160 66 182 72 Q164 72 158 80 Q152 66 144 60Z" fill="${band}"/>
    <circle cx="100" cy="78" r="50" fill="${suit}"/>
    <g clip-path="url(#${id})"><rect x="40" y="42" width="120" height="13" fill="${band}"/></g>
    <rect x="87" y="40" width="26" height="17" rx="3" fill="#C9D1DD" stroke="#6B7688" stroke-width="2"/>
    <circle cx="100" cy="48.5" r="3.5" fill="#6B7688"/>
    <rect x="58" y="66" width="84" height="29" rx="14.5" fill="${skin}"/>
    ${eyes}
    ${afterHead}
    ${front}${vFront}
    </g>
  </svg>`;
}
